import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CONTENT_CDN_CONFIG, InternalTokenService } from '@kempower-nx-workspace/chargeye-content';
import type { GetFeedResponse } from '@kempoweroyj/ngx-content-global-api-client';
import { from, switchMap } from 'rxjs';

const STORAGE_KEY = 'ce-product-updates-last-seen';

/**
 * Tracks "new" product update indicators using a session-based pattern:
 *
 * 1. App starts → `lastSeenTimestamp` signal is read from localStorage (previous session's value).
 * 2. `fetchUnreadCount()` computes the side nav badge count from that timestamp.
 * 3. User opens product updates → `persistForNextSession()` writes the newest item's
 *    timestamp to localStorage, but does NOT update the signal. This keeps "New" pills
 *    and tab counts stable for the entire session.
 * 4. User leaves product updates → `clearVisualState()` hides the side nav badge.
 * 5. Next app load → localStorage has the updated timestamp, so only truly new items show.
 *
 * This "write on enter" pattern (used by GitHub, Slack, Stream Chat) is preferred over
 * writing on page leave or app close because `beforeunload`/`ngOnDestroy` is unreliable
 * (tab crash, mobile kill). Worst-case failure: user sees some "New" pills again next
 * session, which is better than silently marking unseen items as read.
 *
 * Storage is per-device (localStorage) because the indicator is informational, not
 * business-critical. Cross-device sync can be added later by swapping localStorage
 * for a user preferences API without changing the component logic.
 *
 * Industry references:
 * - Beamer changelog widget — visit-level clearing, badge drops to 0 on panel open:
 *   https://www.getbeamer.com/help/how-is-the-unread-notification-badge-count-calculated
 * - Intercom News — same visit-level pattern, suppresses badge on first-ever visit:
 *   https://www.intercom.com/help/en/articles/6362267-getting-started-with-news
 * - PatternFly notification badge design guidelines — per-item vs visit-level guidance:
 *   https://www.patternfly.org/components/notification-badge/design-guidelines/
 * - NN/G indicators, validations, and notifications:
 *   https://www.nngroup.com/articles/indicators-validations-notifications/
 */
@Injectable({ providedIn: 'root' })
export class ProductUpdateBadgeService {
    readonly #http = inject(HttpClient);
    readonly #cdnConfig = inject(CONTENT_CDN_CONFIG, { optional: true });
    readonly #internalToken = inject(InternalTokenService, { optional: true });

    readonly unreadCount = signal(0);
    readonly lastSeenTimestamp = signal<string | null>(this.#readTimestamp());

    /**
     * Fetch feed and compute unread count.
     * Waits for internal token to be ready before making the request.
     */
    fetchUnreadCount(stage: string): void {
        if (!this.#cdnConfig) return;

        const cdnConfig = this.#cdnConfig;
        const internalToken = this.#internalToken;
        from(internalToken?.ready ?? Promise.resolve(null))
            .pipe(
                switchMap(() => {
                    const token = internalToken?.token ?? cdnConfig.deliveryToken;
                    const headers = new HttpHeaders({ 'X-Delivery-Token': token });
                    return this.#http.get<GetFeedResponse>(
                        `${cdnConfig.url}/api/content/cdn/feeds/${stage}`,
                        { headers, params: { limit: '50' } },
                    );
                }),
            )
            .subscribe({
                next: (res) => {
                    const lastSeen = this.lastSeenTimestamp();
                    const items = res.items ?? [];
                    if (lastSeen) {
                        const count = items.filter(
                            (item) => item.publishedAt && item.publishedAt > lastSeen
                        ).length;
                        this.unreadCount.set(count);
                    } else {
                        this.unreadCount.set(items.length);
                    }
                },
                error: () => {
                    // Silently fail — badge is non-critical
                },
            });
    }

    /**
     * Clear visual unread state without persisting to localStorage.
     * Called on page activation so bold/badge clear immediately.
     */
    clearVisualState(): void {
        this.unreadCount.set(0);
    }

    /**
     * Mark all items as read by syncing the signal from localStorage.
     * Called on page leave so unread dots/bold clear immediately in this session.
     */
    markAllRead(): void {
        this.unreadCount.set(0);
        this.lastSeenTimestamp.set(this.#readTimestamp());
    }

    /**
     * Persist last-seen timestamp to localStorage for next session,
     * without updating the signal — keeps pills visible this session.
     */
    persistForNextSession(newestPublishedAt: string): void {
        if (!newestPublishedAt) return;
        this.#writeTimestamp(newestPublishedAt);
    }

    /**
     * Check whether a given publishedAt timestamp is newer than lastSeen.
     */
    isUnread(publishedAt: string): boolean {
        const lastSeen = this.lastSeenTimestamp();
        if (!lastSeen) return true;
        return publishedAt > lastSeen;
    }

    #readTimestamp(): string | null {
        try {
            return localStorage.getItem(STORAGE_KEY);
        } catch {
            return null;
        }
    }

    #writeTimestamp(timestamp: string): void {
        try {
            localStorage.setItem(STORAGE_KEY, timestamp);
        } catch {
            // localStorage unavailable (private browsing, etc.)
        }
    }
}
